<template>
  <v-dialog
    :model-value="true"
    width="400px"
    @update:model-value="close"
  >
    <v-card class="card pt-2 pb-5 px-2 br-5">
      <v-card-text class="pt-2 pb-2 px-4">
        <p class="text-caption text-secondary mb-1">
          Choose which fields to show on the label.
        </p>
        <div class="field-list overflow-y-auto pb-2">
          <div class="py-1">
            <v-btn
              id="selectAllLinkedLabelFields"
              variant="text"
              class="text-primary text-capitalize text-decoration-underline"
              @click="setAll(true)"
            >
              Select All
            </v-btn>
            <v-btn
              id="clearLinkedLabelFields"
              variant="text"
              class="text-primary text-capitalize text-decoration-underline"
              @click="setAll(false)"
            >
              Clear
            </v-btn>
          </div>
          <v-progress-linear
            v-if="loading"
            indeterminate
            color="primary"
          />
          <v-list
            v-else
            density="compact"
            bg-color="transparent"
            class="py-0"
          >
            <ExportSettingOptions
              :tab-content="options"
              @update-setting="updateSetting"
            />
          </v-list>
        </div>
        <p
          v-if="selectedFields.length && !hasValueToShow"
          class="text-caption text-error mt-2 mb-0"
          data-testid="linked-label-no-values"
        >
          None of the selected fields have a value to show.
        </p>
        <div class="d-flex justify-end ga-2 pt-4">
          <v-btn
            id="cancelLinkedLabel"
            variant="outlined"
            @click="close"
          >
            Cancel
          </v-btn>
          <np-action-btn
            id="createLinkedLabel"
            :disabled="loading || !hasValueToShow"
            :loading="creating"
            @click="submit"
          >
            {{ label ? 'Save' : 'Create' }}
          </np-action-btn>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import {mapMutations} from 'vuex';
import type {PropType} from 'vue';
import {wrapError} from '@northpoint-development/labs-common-core';
import ExportSettingOptions from '@/internal/components/FilterMenu/sections/UpsertSharedMap/ExportSettingOptions.vue';
import {
  buildLinkedLabelText,
  createLinkedLabel,
  updateLinkedLabel,
  type LinkedLabelSource,
  type LinkedLabelTarget,
} from '@/services/GraphicService/LabelGraphic/linkedLabel';
import type {ExportFieldSetting} from '@/lib/exportFieldPrefs';
import {
  buildLinkedLabelFieldOptions,
  loadLinkedLabelFieldOptions,
  saveLinkedLabelFieldOptions,
  selectedLinkedLabelFields,
} from './linkedLabelFieldPrefs';

type LinkedLabelFeature = LinkedLabelSource & {featureType: string};

export default {
  name: 'LinkedLabelDialog',
  components: {
    ExportSettingOptions,
  },
  props: {
    feature: {
      type: Object as PropType<LinkedLabelFeature>,
      required: true,
    },
    label: {
      type: Object as PropType<LinkedLabelTarget | null>,
      default: null,
    },
  },
  emits: ['close'],
  data() {
    return {
      options: [] as ExportFieldSetting[],
      loading: true,
      creating: false,
    };
  },
  computed: {
    selectedFields(): string[] {
      return selectedLinkedLabelFields(this.options);
    },
    /** createLinkedLabel and updateLinkedLabel reject a blank label. */
    hasValueToShow(): boolean {
      return buildLinkedLabelText(this.feature.displayFields, this.selectedFields) !== '';
    },
  },
  async created() {
    if (this.label) {
      const shown = new Set(this.label.linkedLabel.fieldLabels);
      this.options = buildLinkedLabelFieldOptions(this.feature.displayFields)
        .map((option) => ({...option, value: shown.has(option.label)}));
    } else {
      this.options = await loadLinkedLabelFieldOptions(this.feature.featureType, this.feature.displayFields);
    }
    this.loading = false;
  },
  methods: {
    ...mapMutations(['setSnack']),
    updateSetting({label, value}: {label: string; value: boolean}) {
      const option = this.options.find((candidate) => candidate.label === label);
      if (option) {
        option.value = value;
      }
    },
    setAll(value: boolean) {
      this.options.forEach((option) => option.value = value);
    },
    close() {
      this.$emit('close');
    },
    async submit() {
      this.creating = true;
      try {
        if (this.label) {
          // Editing one label leaves the remembered selection for new labels alone
          await updateLinkedLabel(this.feature, this.label, this.selectedFields);
        } else {
          await createLinkedLabel(this.feature, this.selectedFields);
          void saveLinkedLabelFieldOptions(this.feature.featureType, this.options);
        }
        this.close();
      } catch (error) {
        console.error(wrapError(error, this.label ? 'Error updating linked label' : 'Error creating linked label'));
        const message = this.label ? 'The label could not be updated.' : 'The label could not be created.';
        this.setSnack({message, color: 'error'});
      } finally {
        this.creating = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.field-list {
  max-height: 300px;
  background-color: rgb(242, 242, 242, 0.5);

  // The fields are a flat list, so drop the space ExportSettingOptions leaves for nested groups' chevrons and line the
  // checkboxes up with the Select All text
  :deep(.export-setting.indent) {
    padding-left: 16px;
  }
  :deep(.export-setting .v-checkbox) {
    margin-left: 0 !important;
  }
  :deep(.export-setting .v-selection-control__wrapper) {
    margin-left: 0;
  }
}
</style>
